import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";

import SiteLayout from "@/components/site/SiteLayout";
import { ProtectedRoute, AdminRoute } from "@/components/auth/ProtectedRoute";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Solutions from "@/pages/Solutions";
import Advertising from "@/pages/Advertising";
import ProposalView from "@/pages/ProposalView";
import Industries from "@/pages/Industries";
import Portfolio from "@/pages/Portfolio";
import PortfolioDetail from "@/pages/PortfolioDetail";
import CaseStudies from "@/pages/CaseStudies";
import Pricing from "@/pages/Pricing";
import Contact from "@/pages/Contact";
import Consultation from "@/pages/Consultation";
import Blog from "@/pages/Blog";
import BlogArticle from "@/pages/BlogArticle";
import Faq from "@/pages/Faq";
import Testimonials from "@/pages/Testimonials";
import Careers from "@/pages/Careers";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";

import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AuthCallback from "@/pages/auth/AuthCallback";

import PortalLayout from "@/components/portal/PortalLayout";
import PortalDashboard from "@/pages/portal/Dashboard";
import PortalProjects from "@/pages/portal/Projects";
import PortalProjectDetail from "@/pages/portal/ProjectDetail";
import PortalProposals from "@/pages/portal/Proposals";
import PortalQuotes from "@/pages/portal/Quotes";
import PortalTickets from "@/pages/portal/Tickets";
import PortalTicketDetail from "@/pages/portal/TicketDetail";
import PortalNotifications from "@/pages/portal/Notifications";
import PortalProfile from "@/pages/portal/Profile";

import AdminLayout from "@/components/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminContent from "@/pages/admin/AdminContent";
import AdminProjects from "@/pages/admin/AdminProjects";
import AdminTickets from "@/pages/admin/AdminTickets";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminAutomations from "@/pages/admin/AdminAutomations";
import AdminOpportunities from "@/pages/admin/AdminOpportunities";
import AdminProposals from "@/pages/admin/AdminProposals";
import AdminAdPricing from "@/pages/admin/AdminAdPricing";

function AppRoutes() {
  const location = useLocation();
  // Google OAuth callback: process session_id synchronously before any route/guard.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route element={<SiteLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/:slug" element={<ServiceDetail />} />
        <Route path="/solutions" element={<Solutions />} />
        <Route path="/advertising" element={<Advertising />} />
        <Route path="/industries" element={<Industries />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/portfolio/:slug" element={<PortfolioDetail />} />
        <Route path="/case-studies" element={<CaseStudies />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/consultation" element={<Consultation />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/testimonials" element={<Testimonials />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/privacy" element={<Legal pageKey="privacy" />} />
        <Route path="/terms" element={<Legal pageKey="terms" />} />
        <Route path="/cookie-policy" element={<Legal pageKey="cookie" />} />
        <Route path="/refund-policy" element={<Legal pageKey="refund" />} />
        <Route path="/disclaimer" element={<Legal pageKey="disclaimer" />} />
      </Route>

      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/admin/login" element={<Login admin />} />
      <Route path="/proposal/:id" element={<SiteLayout />}>
        <Route index element={<ProposalView />} />
      </Route>

      <Route path="/dashboard" element={<ProtectedRoute><PortalLayout /></ProtectedRoute>}>
        <Route index element={<PortalDashboard />} />
        <Route path="projects" element={<PortalProjects />} />
        <Route path="projects/:id" element={<PortalProjectDetail />} />
        <Route path="quotes" element={<PortalQuotes />} />
        <Route path="proposals" element={<PortalProposals />} />
        <Route path="tickets" element={<PortalTickets />} />
        <Route path="tickets/:id" element={<PortalTicketDetail />} />
        <Route path="notifications" element={<PortalNotifications />} />
        <Route path="profile" element={<PortalProfile />} />
      </Route>

      <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="opportunities" element={<AdminOpportunities />} />
        <Route path="leads" element={<AdminLeads />} />
        <Route path="proposals" element={<AdminProposals />} />
        <Route path="ad-pricing" element={<AdminAdPricing />} />
        <Route path="projects" element={<AdminProjects />} />
        <Route path="tickets" element={<AdminTickets />} />
        <Route path="services" element={<AdminContent kind="services" title="Services" />} />
        <Route path="packages" element={<AdminContent kind="packages" title="Packages & Pricing" />} />
        <Route path="portfolio" element={<AdminContent kind="portfolio" title="Portfolio & Case Studies" />} />
        <Route path="blog" element={<AdminContent kind="blog" title="Blog CMS" />} />
        <Route path="testimonials" element={<AdminContent kind="testimonials" title="Testimonials" />} />
        <Route path="faqs" element={<AdminContent kind="faqs" title="FAQs" />} />
        <Route path="automations" element={<AdminAutomations />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>

      <Route path="*" element={<SiteLayout />}>
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
